import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, XCircle, CheckCircle } from "lucide-react";

interface BioValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed?: () => void;
  bioScore: number;
  modalType: 'block' | 'warning';
  feedback?: string;
  isAdmin?: boolean;
}

export const BioValidationModal: React.FC<BioValidationModalProps> = ({
  isOpen,
  onClose,
  onProceed,
  bioScore,
  modalType,
  feedback,
  isAdmin = false
}) => {
  const isBlockModal = modalType === 'block';

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="fixed left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-sm bg-gray-900 border border-gray-800 rounded-xl shadow-2xl">
        <AlertDialogHeader className="space-y-3">
          {/* Ícone + Score */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isBlockModal ? (
                <XCircle className="h-6 w-6 text-red-500" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-yellow-500" />
              )}
              <AlertDialogTitle className={`text-lg font-semibold ${isBlockModal ? 'text-red-400' : 'text-yellow-400'}`}>
                {isBlockModal ? 'Bio Incompleta' : 'Pode Melhorar'}
              </AlertDialogTitle>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${
              isBlockModal ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
            }`}>
              {bioScore}/100
            </div>
          </div>

          {/* Feedback da IA (curto e mobile-first) */}
          <AlertDialogDescription className="text-gray-300 text-sm space-y-2">
            {feedback ? (
              <p className="leading-relaxed">{feedback}</p>
            ) : (
              <>
                {isBlockModal ? (
                  <p>Adicione mais detalhes sobre você, seus interesses e o que busca.</p>
                ) : (
                  <p>Sua bio está boa, mas pode ficar ainda melhor com mais detalhes.</p>
                )}
              </>
            )}
          </AlertDialogDescription>

          {/* Dicas curtas */}
          <div className="bg-gray-800/50 rounded-lg p-3 space-y-1.5">
            <div className="flex items-start gap-2 text-xs text-gray-400">
              <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0 text-green-500" />
              <span>Fale sobre hobbies e interesses</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-400">
              <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0 text-green-500" />
              <span>Descreva o que você busca</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-gray-400">
              <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0 text-green-500" />
              <span>Seja específico e autêntico</span>
            </div>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          {isBlockModal ? (
            isAdmin ? (
              <>
                <AlertDialogCancel 
                  onClick={onClose} 
                  className="w-full sm:w-auto bg-gray-700 hover:bg-gray-600 text-gray-200 border-0"
                >
                  Melhorar
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={onProceed}
                  className="w-full sm:w-auto bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  [ADMIN] Continuar
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction 
                onClick={onClose} 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                Melhorar Biografia
              </AlertDialogAction>
            )
          ) : (
            <>
              <AlertDialogCancel 
                onClick={onClose} 
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white border-0 font-medium"
              >
                Melhorar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={onProceed}
                className="w-full sm:w-auto bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium"
              >
                Continuar Assim
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
